import { getArgumentsFromStoreFieldName } from "@/cache/Utils";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client/react";
import {
  MarginDecisionFragmentDoc,
  MarginDecisionGroupPartialFragmentDoc,
  SubmitMarginDecisionMutation,
  SubmitMarginDecisionMutationVariables,
  SubmitMarginDecisionDocument,
  FetchTenderByIdDocument,
} from "@/graphql/__generated__/graphql";
import { Reference, StoreObject } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { CustomToastComponent } from "@/components/shared";

/**
 * Custom hook for submitting a margin decision and handling the necessary cache updates.
 * @returns {Object} - Contains the submitDecisionMutation function, loading state, and error state.
 */
export default function useSubmitMarginDecisionMutationHook(): {
  submitMarginDecisionMutation: any;
  loading: any;
  error: any;
  } {
  const { t } = useTranslation(["toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const [submitMarginDecisionMutation, { loading, error }] = useMutation<SubmitMarginDecisionMutation, SubmitMarginDecisionMutationVariables>(
    SubmitMarginDecisionDocument, {
      onCompleted: () => {
        toast(CustomToastComponent, {
          toastId: "margin-decision-submit-success",
          data: {
            title: t("toasts.margindecisionsuccess"),
            type: "success",
          },
        });
      },
      refetchQueries: [
        { query: FetchTenderByIdDocument, variables: { id: Number(tenderID) } },
      ],
      update: (cache, { data }) => {
        const newMarginDecision = data?.submitMarginDecision.marginDecision;
        const newMarginDecisionGroup = data?.submitMarginDecision.marginDecisionGroup;

        // Update the MarginDecisionGroup with the new fields
        if (newMarginDecisionGroup?.id != null && newMarginDecisionGroup?.autoApproved != null) {
          cache.writeFragment({
            id: cache.identify({ __typename: "MarginDecisionGroup", id: newMarginDecisionGroup.id }),
            fragment: MarginDecisionGroupPartialFragmentDoc,
            data: {
              __typename: "MarginDecisionGroup",
              id: newMarginDecisionGroup.id,
              autoApproved: newMarginDecisionGroup.autoApproved,
              awaitingOrganizationId: newMarginDecisionGroup.awaitingOrganizationId ?? null,
              currentDecision: newMarginDecisionGroup.currentDecision ?? null,
            }
          });
        }

        /**
       * Update the cache for the 'fetchMarginDecisionsByGroup' query.
       * We make use of the 'storeFieldName' from Apollo in order to update the correct cache field.
       ** DO NOT MODIFY THIS CODE BLOCK.
       */
        cache.modify({
          fields: {
            fetchMarginDecisionsByGroup(existingData = {}, { storeFieldName, readField }) {
            // Extract arguments from 'storeFieldName'
              const args = getArgumentsFromStoreFieldName(storeFieldName);

              // If arguments are missing or don't match, return existing data
              if (!args || args.marginDecisionGroupId !== newMarginDecision?.marginDecisionGroupId) {
                return existingData;
              }

              const existingMarginDecisions = existingData.marginDecisions || [];

              // Check for duplicates
              if (
                existingMarginDecisions.some(
                  (decisionRef: Reference | StoreObject | undefined) => readField("id", decisionRef) === newMarginDecision?.id
                )
              ) {
                return existingData;
              }

              // Write the new margin decision to the cache
              const newMarginDecisionRef = cache.writeFragment({
                data: newMarginDecision,
                fragment: MarginDecisionFragmentDoc,
              });

              // Return the updated data with the new margin decision added
              return {
                ...existingData,
                marginDecisions: [...existingMarginDecisions, newMarginDecisionRef],
              };
            },
          },
        });
      },
    });

  return {
    submitMarginDecisionMutation,
    loading,
    error,
  };
}
